import { Menu, Transition } from "@headlessui/react";
import { BellIcon } from "@heroicons/react/outline";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

/**
 * Generic, source-agnostic notification bell shell.
 *
 * It owns the bell icon, the unread dot, and the popover chrome, and fires
 * `onOpen` once each time the popover opens. It knows nothing about what the
 * items are — a caller (e.g. ReleaseBell) feeds the title, the unread count,
 * the rows (children) and an optional footer. A future notification kind can
 * reuse this same shell without a rewrite.
 *
 * Chrome + transitions mirror `common/components/Icon/ThreeDotsDropdownMenu.tsx`.
 * The unread dot reuses the corner-dot precedent from
 * `features/deviceInstaller/components/InstallOnComputerButton.tsx` (red here).
 */

interface NotificationBellProps {
  unreadCount: number;
  onOpen: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

const OpenWatcher = ({ open, onOpen }: { open: boolean; onOpen: () => void }) => {
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      onOpen();
    }
    wasOpen.current = open;
  }, [open, onOpen]);
  return null;
};

const NotificationBell = ({
  unreadCount,
  onOpen,
  title,
  children,
  footer,
}: NotificationBellProps) => {
  return (
    <Menu as="div" className="relative">
      {({ open }: { open: boolean }) => (
        <>
          <OpenWatcher open={open} onOpen={onOpen} />
          <Menu.Button className="relative" aria-label={title}>
            <BellIcon className="h-6 w-6 text-gray-600 hover:text-gray-900" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-white bg-red-500" />
            )}
          </Menu.Button>

          <Transition
            show={open}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right bg-white border border-gray-200 rounded-md shadow-lg outline-none z-50">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-black">{title}</p>
              </div>
              <div className="max-h-96 overflow-y-auto">{children}</div>
              {footer && <div className="border-t border-gray-100">{footer}</div>}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default NotificationBell;
